import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import Cookies from "js-cookie";

import {
  ApiRoutes,
  baseUrl as baseURL,
  token,
  refreshToken,
  AUTH_STORAGE_KEYS,
} from "../constants";
import { logError } from "../utils/sentry";

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const axiosInstance = axios.create({
  baseURL,
  // Without a timeout, a hung/slow request never rejects — React Query's
  // `isLoading` then stays true forever with no error and no toast, which
  // reads to the user as a permanently "stuck" spinner.
  timeout: 20_000,
});

// Single-flight refresh: when several requests 401 at once, only the first
// performs the refresh; the rest queue and reuse its result. Without this,
// concurrent refreshes race and rotating refresh tokens invalidate each other.
let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (newToken: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, newToken: string | null = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (newToken) {
      resolve(newToken);
    } else {
      reject(error);
    }
  });
  pendingQueue = [];
};

const clearAuthStorage = () => {
  Cookies.remove(token);
  Cookies.remove(refreshToken);
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  sessionStorage.clear();
};

const forceLogout = () => {
  clearAuthStorage();
  window.location.replace("/login");
};

const performTokenRefresh = async (): Promise<string> => {
  const refreshTokenValue = Cookies.get(refreshToken);
  if (!refreshTokenValue) {
    throw new Error("No refresh token available");
  }

  const response = await axios.post(`${baseURL}${ApiRoutes.REFRESH_TOKEN}`, {
    refreshToken: refreshTokenValue,
  });

  // API responses wrap payloads in a `data` envelope; tolerate both shapes
  const payload = response?.data?.data ?? response?.data;
  const newToken = payload?.accessToken;
  const newRefreshToken = payload?.refreshToken;

  if (!newToken || !newRefreshToken) {
    throw new Error("Invalid refresh response");
  }

  Cookies.set(token, newToken);
  Cookies.set(refreshToken, newRefreshToken);
  return newToken;
};

const onRequest = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  const tokenValue = Cookies.get(token);
  if (tokenValue) {
    config.headers["Authorization"] = `Bearer ${tokenValue}`;
    config.withCredentials = true;
  }

  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

const onResponseError = async (error: AxiosError) => {
  const originalRequest = error.config as ExtendedAxiosRequestConfig;
  const currentToken = Cookies.get(token);

  // Only handle 401s for authenticated sessions. Unauthenticated 401s
  // (wrong login credentials) must surface to the caller untouched.
  if (error.response?.status !== 401 || !originalRequest || !currentToken) {
    return Promise.reject(error);
  }

  if (originalRequest._retry) {
    // Already retried once after a refresh — the session is truly invalid
    forceLogout();
    return Promise.reject(error);
  }
  originalRequest._retry = true;

  if (isRefreshing) {
    // A refresh is already in flight — wait for it and reuse its token
    try {
      const newToken = await new Promise<string>((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      });
      originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    } catch (queueError) {
      return Promise.reject(queueError);
    }
  }

  isRefreshing = true;
  try {
    const newToken = await performTokenRefresh();
    processQueue(null, newToken);
    originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
    return axiosInstance(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError);
    logError(refreshError);
    forceLogout();
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
};

// https://axios-http.com/docs/interceptors
axiosInstance.interceptors.request.use(onRequest, onRequestError);
axiosInstance.interceptors.response.use(
  (response) => response,
  onResponseError,
);

export default axiosInstance;
